import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, Shield, Smartphone, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import API from '@/utils/api';

const TwoFactorVerifyPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginDirect } = useAuth();
  
  const pendingToken = location.state?.pendingToken;
  const totpEnabled = location.state?.totpEnabled;
  const emailEnabled = location.state?.emailEnabled;
  
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [activeTab, setActiveTab] = useState(totpEnabled ? 'totp' : 'email');

  // Redirect if no pending token
  if (!pendingToken) {
    navigate('/login');
    return null;
  }

  const handleVerify = async () => {
    if (code.length !== 6) {
      toast.error('Please enter a 6-digit code');
      return;
    }
    
    setLoading(true);
    try {
      const response = await API.post('/auth/verify-2fa', {
        pending_token: pendingToken,
        code: code,
        method: activeTab
      });
      
      // Login successful
      loginDirect(response.data.user, response.data.session_token);
      toast.success('Login successful!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setResending(true);
    try {
      await API.post('/auth/resend-2fa-code', { pending_token: pendingToken });
      toast.success('New code sent to your email');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend code');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2">
            <Shield className="h-6 w-6 text-blue-600" />
          </div>
          <CardTitle>Two-Factor Authentication</CardTitle>
          <CardDescription>
            Enter the verification code to continue
          </CardDescription>
        </CardHeader>
        <CardContent>
          {totpEnabled && emailEnabled ? (
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-2 mb-4">
                <TabsTrigger value="totp" className="flex items-center gap-2">
                  <Smartphone className="h-4 w-4" />
                  Authenticator
                </TabsTrigger>
                <TabsTrigger value="email" className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  Email
                </TabsTrigger>
              </TabsList>
              
              <TabsContent value="totp">
                <p className="text-sm text-gray-500 mb-4">
                  Enter the 6-digit code from your authenticator app
                </p>
              </TabsContent>
              
              <TabsContent value="email">
                <p className="text-sm text-gray-500 mb-4">
                  Enter the 6-digit code sent to your email
                </p>
              </TabsContent>
            </Tabs>
          ) : (
            <div className="mb-4">
              {totpEnabled ? (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Smartphone className="h-4 w-4" />
                  Enter the code from your authenticator app
                </div>
              ) : (
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Mail className="h-4 w-4" />
                  Enter the code sent to your email
                </div>
              )}
            </div>
          )}
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="code">Verification Code</Label>
              <Input
                id="code"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={6}
                placeholder="000000"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                className="text-center text-2xl tracking-widest"
                autoFocus
              />
            </div>
            
            <Button 
              onClick={handleVerify} 
              disabled={loading || code.length !== 6}
              className="w-full"
            >
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Verify
            </Button>
            
            {(emailEnabled && activeTab === 'email') && (
              <Button 
                variant="ghost" 
                onClick={handleResendCode}
                disabled={resending}
                className="w-full"
              >
                {resending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Resend Code
              </Button>
            )}
            
            <Button 
              variant="link" 
              onClick={() => navigate('/login')}
              className="w-full"
            >
              Back to Login
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default TwoFactorVerifyPage;
